import { GenerateImageOptions, GenerateImageRendererOptions, loadModelFile, numberOfGeneratedImages } from "@centricsoftware/model-viewer";
import { isEncrypted } from "csi/model/document";
import { getImageSrc } from "csi/model/image";
import { PATH_CURRENT_REVISION } from "csi/reflection/reflectionConstants";
import { baseGeometryMap, GenerateImageFormat, GenerateImageType, imageRatioMap, MaterialSide as MaterialSideEnum, normalizeExposure, SnapshotMimeType } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { decryptFileChunks } from "csi/threeD/utility/avatarDecrypt";
import { getAlternateFileOnRevisionNode, getFileType, getModelUrl, getModelViewerLoaderOptions, getResourceURLModifier } from "csi/threeD/utility/resources";
import { C8Node, C8NodeType, cnl, ICache } from "csi/typings";
import { parseHexAlpha } from "csi/utility/colorUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import { GoToDialogHelper } from "../goToDialog/GoToDialogHelper";
import { generateAndUploadImagesToNode, getDocumentPath, ImageConfig, ImageDataMap, prepareImageOptions, TemplateConfig } from "./imageGenerateService";

export type ErrorFunction = (error: Error) => void;

export const LIMIT_FOR_MULTISELECT = 5;
export default class Generate3DImagesDialogStore extends ReactionStore {
	/** cache from controller */
	readonly cache: ICache;

	/** node type the 3D images will be saved to */
	readonly nodeType: C8NodeType;

	/** product type of the Style or Material */
	readonly productType: C8NodeType;

	/** url root */
	readonly urlRoot: cnl;

	/** locale provider */
	readonly loc: ILocaleProvider;

	/** OnCancel function from controller */
	readonly onCancel: VoidFunction;

	/** OnSave function from controller */
	readonly onSave: VoidFunction;

	/** Go To Dialog Helper Documents */
	documentsGoToDialogHelper: GoToDialogHelper;

	/** Go To Dialog Helper Templates */
	templatesGoToDialogHelper: GoToDialogHelper;
	
	/** document URLs */
	@observable.ref documentUrls: cnl[] = [];

	/** template URLs */
	@observable.ref templateUrls: cnl[] = [];

	/** Selected Document URLs */
	@observable selectedDocumentUrls: cnl[] = [];

	/** Selected Template URLs */
	@observable selectedTemplateUrls: cnl[] = [];

	/** PlaceHolder for generated images */
	@observable.ref images: ImageDataMap[] = [];

	/** Number of Images to be generated */
	@observable numberOfImages: number = 0;

	/** Number of Generated Images- used for Generate Button text */
	@observable numberOfImagesText: string = "";

	/** A list of any combination of fileName - templateName that results in an image count of 0 */
	@observable.ref invalidCombinations: string[] = [];

	/** Indicate if the number of images is loading */
	@observable isLoadingDocuments: boolean = false;

	/** Indicate if calculating number of images */
	@observable isCalculatingNumberOfImages: boolean = false;

	/** Selected Documents Configuration */
	@observable.ref selectedDocumentsConfig: ImageConfig[] = [];

	/** Cache the image configs */
	selectedDocumentsCache = new Map<cnl, ImageConfig>();

	/** Determine if the images are in the process of being generated and uploaded */
	@observable isLoadingImages: boolean = false;

	/** Error message */
	@observable errorOnSave: Error = null;

	/** Determine is the user has canceled the generate images operation */
	@observable operationHasAborted = false;

	/** Abort Controller */
	abortController = new AbortController();

	/** get the documents associated to the node via cache or query */
	getDocumentNodes = async (): Promise<void> => {
    	const { urlRoot, nodeType } = this;

    	const search = new Search().setLimitPath(urlRoot, getDocumentPath(nodeType))
    		.addDependencyPaths([PATH_CURRENT_REVISION]);
    	search.getQuery()
    		.Attr("Is3D", xml.OP_EQUALS, true)
    		.Attr("VariantName", xml.OP_EMPTY_STRING, null);

    	const response = await search.getRequest()
    		.go();

    	runInAction(() => {
    		this.documentUrls = response.getResultUrls();
    		this.cache.cacheResponse(response);
    	});
	};

	/** get the templates associated to the node Product Type via query */
	getTemplateNodes = async (): Promise<void> => {
		const { templateNodeType, productType } = this;
		const nodeType = this.nodeType === "Material" ? "Material" : "Style";
		const search = new Search()
			.byType(templateNodeType);
		search.getQuery()
			.Attr("Active", xml.OP_EQUALS, true)
			.Or()
			.RefAttr(`${nodeType}Types`, xml.OP_EQUALS, productType)
			.RefAttr(`${nodeType}Types`, xml.OP_NULL);
		
		const response = await search.getRequest()
			.go();

		runInAction(() => {
			this.templateUrls = response.getResultUrls();
			this.cache.cacheResponse(response);
		});
	};

	/** Template Node Type */
	get templateNodeType(): C8NodeType {
		return this.nodeType === "Material" ? "MaterialSnapshotTemplate" : "StyleSnapshotTemplate";
	}

	get isLoadingNumberOfImages(): boolean {
		return this.isLoadingDocuments || this.isCalculatingNumberOfImages;
	}

	/** Determine if a user can Generate Images */
	get canGenerateImages(): boolean {
		const { 
			documentsExceedMaxValue, 
			numberOfImages, 
			selectedDocumentComponents,
			selectedTemplateComponents, 
			templatesExceedMaxValue,
		 } = this;
		return !selectedDocumentComponents.length || !selectedTemplateComponents.length || numberOfImages === 0 || documentsExceedMaxValue || templatesExceedMaxValue;
	}
	
	/** Return all document component items */
	@computed get documentComponents(): ComponentItem[] {
		const { documentUrls, cache, loc } = this;
		const documentNodes = cache.getNodes(documentUrls);
		return documentNodes.map((docNode) => ({
			id: docNode.$URL,
			name: loc.getNameOrUnnamed(docNode.$Name),
			key: docNode.$URL,
			name2: `${loc.resource("Revision")} ${cache.get(docNode.CurrentRevision).$Name}`,
			imageSrc: getImageSrc(cache.get(docNode.CurrentRevision)),      				
		}));
	}

	/** Return all the template component item */
	@computed get templateComponents(): ComponentItem[] {
		const { cache, templateUrls } = this;
		const templateNodes = cache.getNodes(templateUrls);
		return templateNodes.map((templateNode) => ({
			id: templateNode.$URL,
			name: templateNode.$Name,
			name2: templateNode.Description,
		}));
	}
	
	/** Return all selected document component items */
	@computed get selectedDocumentComponents(): ComponentItem[] {
		const { documentComponents, selectedDocumentUrls } = this;
		return documentComponents.filter((doc) => selectedDocumentUrls.includes(doc.id));
	}
	
	/** Return all selected template component items */
	@computed get selectedTemplateComponents(): ComponentItem[] {
		const { templateComponents, selectedTemplateUrls } = this;
		return templateComponents.filter((template) => selectedTemplateUrls.includes(template.id));
	}

	/** Fetch selected documents model data and create configuration options */
	async loadSelectedDocumentsConfig(): Promise<void> {
		const { selectedDocumentsCache, selectedDocumentUrls, loc, cache } = this;

		const selectedDocumentsConfig = await Promise.all(selectedDocumentUrls.map(async (docUrl) => {
			if (selectedDocumentsCache.has(docUrl)) {
				return selectedDocumentsCache.get(docUrl);
			}
			const docNodeName = loc.getNameOrUnnamed(cache.get(docUrl).$Name);
			const revisionNode = this.selectedRevisionNode(docUrl);
			const fileIsEncrypted = isEncrypted(revisionNode);
			let fileName;
			
			const resourceURLModifier = getResourceURLModifier(revisionNode);
			if (fileIsEncrypted) {
				const fileChunks = revisionNode.FileChunks;
				const decryptedChunks = await decryptFileChunks(fileChunks);
				fileName = decryptedChunks;
			} else {
				fileName = getAlternateFileOnRevisionNode(revisionNode);
			}
			const fileType = getFileType(revisionNode, fileName, fileIsEncrypted);
			const modelUrl = getModelUrl(fileName, revisionNode);
			
			const modelViewerLoaderOptions = getModelViewerLoaderOptions();
			modelViewerLoaderOptions.resourceURLModifier = resourceURLModifier;
			modelViewerLoaderOptions.fileType = fileType;
			
			const modelData = await loadModelFile(modelUrl, modelViewerLoaderOptions);

			const config = {
				modelData,
				revisionNode,
				fileType,
				fileName,
				docNodeName,
			};

			selectedDocumentsCache.set(docUrl, config);
			return config;
		}));

		runInAction(() => {
			this.selectedDocumentsConfig = selectedDocumentsConfig;
			this.isLoadingDocuments = false; 
		});
	}

	/** Gather all the data needed to generate images for multiple templates */
	@computed get selectedTemplatesConfig(): TemplateConfig[] {
		const { selectedTemplateUrls, cache } = this;
		return selectedTemplateUrls.map((templateUrl) => {
			const templateNode = cache.get(templateUrl);
			const imageOptions = this.imageOptions(templateNode);
			const rendererOptions = this.rendererOptions(templateNode);
			const includeAllVariants = templateNode.IncludeAllVariants;
			const templateName = templateNode.$Name;
			
			return {
				imageOptions,
				includeAllVariants,
				rendererOptions,
				templateName,
			};
		});
	}

	/** Determine if the selected documents exceeds the maximum */
	get documentsExceedMaxValue(): boolean {
		return this.selectedDocumentUrls.length > LIMIT_FOR_MULTISELECT;
	}

	/** Determine if the selected templates exceeds the maximum */
	get templatesExceedMaxValue(): boolean {
		return this.selectedTemplateUrls.length > LIMIT_FOR_MULTISELECT;
	}

	/** Update Selected Document URLs */
	@action updateSelectedDocumentUrls = async (event, values: ComponentItem[]): Promise<void> => {
		this.isLoadingDocuments = true;
		this.selectedDocumentUrls = values.map((value) => value.id);
		await this.loadSelectedDocumentsConfig();
	};
	
	/** Update Selected Template URLs */
	@action updateSelectedTemplateUrls = (event, values: ComponentItem[]): void => {
		this.selectedTemplateUrls = values.map((value) => value.id);
	};
	
	/** Set the generated images */
	@action setImages = (images: ImageDataMap[]): void => {
		this.images = [...this.images, ...images];
	};

	/** Set the number of images */
	@action setNumberOfImages = (num: number): void => {
		this.numberOfImages = num;
	};

	/** Set the number of images text */
	@action setNumberOfImagesText = (text: string): void => {
		this.numberOfImagesText = text;
	};

	/** Set the loading state for generating images */
	@action setLoadingImages = (isLoading: boolean): void => {
		this.isLoadingImages = isLoading;
	};

	/** Set the error message */
	@action setErrorOnSave = (error: Error): void => {
		this.errorOnSave = error;
	};

	/** Close error dialog and close react modals */
	@action closeErrorDialog = (): void => {
		this.errorOnSave = null;
		this.onCancel();
	};

	/** Set flag to determin if the user has canceled the generate images operation */
	@action setOperationHasAborted = (value: boolean): void => {
		this.operationHasAborted = value;
	};

	/** External handler to abort generate and upload of images */
	@action abortImageGeneration = ():void => {
		this.abortController.abort();
		this.setOperationHasAborted(true);
	};

	/** Combine template name and file names to display a list of invalid combinations */
	@action setInvalidCombinations = (combinations: {docNodeName: string; templateName: string}[]): void => {
		const stringCombinations = combinations.map((comb) => `${comb.docNodeName} - ${comb.templateName}`);
		this.invalidCombinations = stringCombinations;
	}; 

	/** Generate Images and upload them to the parent node */
	generateImages = async (): Promise<void> => {
		const { 
			dispose,
			onSave,
			selectedDocumentsConfig,
			selectedTemplatesConfig,
			setErrorOnSave,
			setLoadingImages,
			urlRoot,
		 } = this;
		setLoadingImages(true);
		const resolvedSelectedDocumentsConfig = await Promise.all(selectedDocumentsConfig);
		await Promise.race([
			generateAndUploadImagesToNode(resolvedSelectedDocumentsConfig, selectedTemplatesConfig, urlRoot, this.abortController.signal),
			new Promise((_, reject) => {
				this.abortController.signal.addEventListener("abort", () => {
					reject(new Error("Operation aborted"));
				});
			}), 
		  ])
			.then(() => {
				setLoadingImages(false);
				onSave();
				dispose();	
			})
			.catch((e) => {
				setLoadingImages(false);
				setErrorOnSave(e);
			});
	};
	
	/** OnClick handler for Documents Go To Dialog */
	documentsGoToDialogAction = (): void => {
		const { documentUrls, selectedDocumentUrls, cache, documentsGoToDialogHelper } = this;
		const selectedDocumentNodes = cache.getNodes(selectedDocumentUrls);
		documentsGoToDialogHelper.openGoToDialogWithCNLs(documentUrls, selectedDocumentNodes);
	};

	/** OnClick handler for Templates Go To Dialog */
	templatesGoToDialogAction = (): void => {
		const { templateUrls, selectedTemplateUrls, cache, templatesGoToDialogHelper } = this;
		const selectedNodes = cache.getNodes(selectedTemplateUrls);
		templatesGoToDialogHelper.openGoToDialogWithCNLs(templateUrls, selectedNodes);
	};
	
	/** Return the Image Options from a selected template */
	imageOptions(templateNode: C8Node): GenerateImageOptions {
		const { cache } = this;

		if (!templateNode) {
			return null;
		}	
		
		const {
			CameraPositions,
			ImageType,
			ImageCount,
			BaseGeometry,
			MaterialSide,
		} = templateNode;

		const imageType = xml.enumGetText(ImageType) === GenerateImageType.Image ? GenerateImageType.Image : GenerateImageType.Turntable;
		const imageCount = imageType === GenerateImageType.Turntable ? xml.enumGetText(ImageCount) : "";
		const geometry = BaseGeometry?.map((item) => baseGeometryMap[xml.enumGetText(item)]);
		const materialSide = MaterialSide?.map((item) => MaterialSideEnum[xml.enumGetText(item)]);
		const turntable = imageType === GenerateImageType.Turntable ? {
			numberOfImages: parseInt(imageCount),
		} : undefined;
		const cameraPositionNodes = cache.getNodes(CameraPositions);
		const cameraPositions = cameraPositionNodes.map((node) => ({
			id: node.$URL,
			name: node.$Name,
			position: node.Location,
			rotation: node.Rotation,
		}));

		return {
			camera: {
				cameraPositions,
				turntable,
			},
			materials: {
				variants: [], // Variants are determined in generateImages
				sides: materialSide,
				primitives: geometry,
			},
		};
	}

	rendererOptions(templateNode: C8Node): GenerateImageRendererOptions {
		const { cache } = this;
		if (!templateNode) {
			return null;
		}
		const {
			BackgroundColor,
			ImageRatio,
			ImageFormat,
			ThreeDEnvironment,
			Use3DEnvironmentAsBackground,
		} = templateNode;

		const environmentNode = cache.get(ThreeDEnvironment);
		const envFile = environmentNode?.EnvironmentFile;
		const backgroundOrientation = environmentNode?.Orientation ? (environmentNode.Orientation * -1) : 0;
		const toneMappingExposure = environmentNode?.Exposure ? normalizeExposure(environmentNode.Exposure) : 1;
		const environment = Use3DEnvironmentAsBackground && envFile ? getFileVaultRequestUrl(envFile, false) : null;
		const imageType = xml.enumGetText(ImageFormat) === GenerateImageFormat.JPEG ? SnapshotMimeType.JPEG : SnapshotMimeType.PNG;
		const imageRatio = imageRatioMap[xml.enumGetText(ImageRatio)];
		const { rgb, alpha } = parseHexAlpha(BackgroundColor);
		const background = Use3DEnvironmentAsBackground && envFile ? environment : rgb;

		return {
			background,
			backgroundIntensity: alpha,
			backgroundOrientation,
			environment,
			imageRatio,
			imageType,
			toneMappingExposure,
		};
	}

	/** Return the revision nodes for all selected documents */
	selectedRevisionNode(docUrl: cnl): C8Node {
		const { cache } = this;
		return cache.getPathNode(docUrl, "Child:CurrentRevision");
	}
	
	/** Calculate the number of images that will be generated */
	numberOfGeneratedImages = (allImageOptions: Omit<TemplateConfig, "rendererOptions">[]): number => {
		if (!allImageOptions.length) {
			return 0;
		}
		let count = 0;
		const invalidCombinations = [];
		allImageOptions.forEach((config) => {
			const { imageOptions, includeAllVariants, templateName } = config;
			this.selectedDocumentsConfig.forEach((documentConfig) => {
				const { docNodeName, fileType, modelData: { gltfData, modelData } } = documentConfig;
				const isU3M = fileType === "u3m";
				const deepCopyOfImageOptions = prepareImageOptions(
					imageOptions,
					includeAllVariants,
					gltfData,
				  );
				const num = numberOfGeneratedImages(deepCopyOfImageOptions, isU3M, modelData);
				if (num === 0) {
					invalidCombinations.push({
						docNodeName,
						templateName,
					});
				}
				count += num;
			});
		});
		this.setInvalidCombinations(invalidCombinations);
		return count;
	};

	/** Initialization function */
	init = async (): Promise<void> => {
		try {
			await this.getDocumentNodes();
			await this.getTemplateNodes();
		} catch (e) {
    		console.warn("Error fetching document urls", e);
    	}
	};

	/** Dispose of all resources */
	@action dispose = (): void => {
		this.selectedDocumentsCache.clear();
		this.selectedDocumentUrls = [];
		this.selectedDocumentsConfig = [];
		this.selectedTemplateUrls = [];
	};

	constructor(cache: ICache, loc:ILocaleProvider, nodeType: C8NodeType, urlRoot: cnl, productType: C8NodeType, onCancel: VoidFunction, onSave: VoidFunction) {
    	super();
    	makeObservable(this);
    	this.cache = cache;
    	this.loc = loc;
    	this.nodeType = nodeType;
    	this.urlRoot = urlRoot;
		this.productType = productType;
		this.onCancel = onCancel;
		this.onSave = onSave;
		
    	this.init();

		this.documentsGoToDialogHelper = new GoToDialogHelper(
			"Document",
			true,	
			action((selectedUrls: cnl[]) => {
				const selectedDocumentComponents = this.documentComponents.filter((doc) => selectedUrls.includes(doc.id));
				this.updateSelectedDocumentUrls(null, selectedDocumentComponents);
			}),
		);

		this.templatesGoToDialogHelper = new GoToDialogHelper(
			this.templateNodeType,
			true,
			action((selectedUrl: cnl[]) => {
				const selectedTemplateComponents = this.templateComponents.filter((template) => selectedUrl.includes(template.id));
				this.updateSelectedTemplateUrls(null, selectedTemplateComponents);
			}),
		);
		
		this.trackReaction(reaction(
			() => [
				this.selectedDocumentsConfig,
				this.selectedTemplatesConfig,
				this.isLoadingDocuments,
			],
			() => {
				if (this.isLoadingDocuments) {
					// If documents are still loading, defer calculation
					return;
				}
				this.isCalculatingNumberOfImages = true;
				const allImageOptions = this.selectedTemplatesConfig.map((template) => {
					return { 
						imageOptions: template.imageOptions,
						includeAllVariants: template.includeAllVariants,
						templateName: template.templateName,
					};
				});
				const numbOfImages = this.numberOfGeneratedImages(allImageOptions);
				this.setNumberOfImages(numbOfImages);
				this.setNumberOfImagesText(numbOfImages > 0 ? `(${numbOfImages})` : "");
				this.isCalculatingNumberOfImages = false;
			},
		));
	}
}

export const Generate3DImagesDialogStoreContext = createContext<Generate3DImagesDialogStore>(undefined);
export const useGenerate3DImagesDialogStore = (): Generate3DImagesDialogStore => useContext(Generate3DImagesDialogStoreContext);